import { useEffect, useState } from "react";
import { UserPlus, Clock, RefreshCw, Gavel, HelpCircle } from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import { StatBars } from "@/components/StatBars";
import { CLASS_META } from "@/lib/game";
import TraitSelectionModal from "@/components/TraitSelectionModal";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const CLASS_BUILD_HINTS = {
  Provocator: {
    bestStats: "Defense, Stamina, Strength",
    bestStance: "Defensive",
  },
  Dimachaerus: {
    bestStats: "Speed, Strength, Precision",
    bestStance: "Aggressive",
  },
  Retiarius: {
    bestStats: "Technique, Precision, Speed",
    bestStance: "Balanced",
  },
};

const POTENTIAL_COLORS = {
  Average: "text-orange-300 border-orange-800/50 bg-orange-950/20",
  Good: "text-yellow-300 border-yellow-800/50 bg-yellow-950/20",
  Excellent: "text-emerald-300 border-emerald-800/50 bg-emerald-950/20",
  Elite: "text-sky-300 border-sky-800/50 bg-sky-950/20",
};

function Countdown({ to }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const diff = Math.max(0, new Date(to).getTime() - now);
  const h = Math.floor(diff / 3.6e6), m = Math.floor((diff % 3.6e6) / 6e4), s = Math.floor((diff % 6e4) / 1000);
  return <span className="tabular-nums">{h}h {m}m {s}s</span>;
}

function RecruitCard({ recruit, busy, full, onRecruit }) {
  const meta = CLASS_META[recruit.cls] || {};
  const Icon = meta.icon;
  const hint = CLASS_BUILD_HINTS[recruit.cls];
  const isVeteran = recruit.recruit_type === "veteran";
  const potentialClass = POTENTIAL_COLORS[recruit.potential_grade] || "text-amber-200 border-amber-800/50 bg-amber-950/20";

  return (
    <div data-testid={`recruit-card-${recruit.id}`} className={`bg-zinc-950 border p-4 ${isVeteran ? "border-stone-600/70" : "border-amber-700/30"}`}>
      <div className="flex justify-between items-start gap-3">
        <div className="flex items-center gap-2 min-w-0">{Icon && <Icon className={meta.color} size={22} />}
          <span className="text-[11px] uppercase tracking-widest text-zinc-500">{meta.tag}</span>
          {hint && (
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" data-testid={`recruit-class-build-trigger-${recruit.id}`} aria-label={`${recruit.cls} recommended build`}
                  className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-red-500/80 bg-red-950/80 text-[11px] font-bold leading-none text-red-200 shadow-[0_0_12px_rgba(239,68,68,0.45)] transition-colors hover:border-red-400 hover:bg-red-900/90 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400/80">?
                </button>
              </PopoverTrigger>
              <PopoverContent data-testid={`recruit-class-build-tooltip-${recruit.id}`} align="start" className="w-64 border-amber-700/40 bg-zinc-950 px-3 py-2 text-zinc-100 shadow-[0_12px_32px_rgba(0,0,0,0.45)]">
                <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300">Recommended build</div>
                <div className="mt-1 text-xs text-zinc-300">Best stats: {hint.bestStats}</div>
                <div className="mt-1 text-xs text-zinc-300">Best stance: {hint.bestStance}</div>
              </PopoverContent>
            </Popover>
          )}
        </div>
        <span data-testid={`recruit-type-${recruit.id}`} className={`shrink-0 px-2 py-0.5 text-[10px] uppercase tracking-widest border ${isVeteran ? "border-stone-600/70 text-stone-300 bg-stone-900/40" : "border-violet-800/50 text-violet-400 bg-violet-900/20"}`}>
          {isVeteran ? "Veteran" : "Fresh"}
        </span>
      </div>
      <h3 className="font-cinzel text-lg text-amber-300 tracking-wide mt-3 uppercase truncate">{recruit.name}</h3>
      <p className="text-xs text-zinc-500 mt-1">{recruit.class_info?.weapon}{recruit.class_info?.offhand ? ` + ${recruit.class_info.offhand}` : ""}</p>
      {isVeteran ? (
        <div className="mt-3 space-y-2 text-xs">
          <span data-testid={`veteran-age-${recruit.id}`} className="inline-block border border-zinc-800 bg-zinc-900/40 px-2 py-1.5 text-zinc-300">Age <strong className="text-stone-200">{recruit.age}</strong></span>
          <span data-testid={`veteran-scout-progress-${recruit.id}`} className="block text-zinc-500">{Math.round((recruit.scout_progress || 0) * 100)}% scouted</span>
          <span data-testid={`veteran-potential-${recruit.id}`} className={`block border px-2 py-1.5 ${potentialClass}`}>Potential range: <strong>{recruit.potential_range}</strong></span>
          <span data-testid={`veteran-starter-skill-points-${recruit.id}`} className="block text-amber-200">{recruit.starter_skill_points || 0} starter skill points</span>
        </div>
      ) : (
        <span data-testid={`fresh-potential-${recruit.id}`} className="mt-3 inline-flex px-2 py-0.5 text-[10px] uppercase tracking-widest border border-violet-800/50 text-violet-400 bg-violet-900/20">Potential unscouted</span>
      )}
      <div className="mt-4"><StatBars skills={recruit.skills} compact /></div>
      <button data-testid={`recruit-btn-${recruit.id}`} disabled={busy || full} onClick={() => onRecruit(recruit.id)} className={btn() + " w-full mt-4"}>
        {full ? "Roster Full" : "Recruit"}
      </button>
    </div>
  );
}

export default function Recruit() {
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showPotentialHelp, setShowPotentialHelp] = useState(false);
  const [newRecruitForTraits, setNewRecruitForTraits] = useState(null);
  const [recruitFilter, setRecruitFilter] = useState("all");

  const load = () => api.get("/recruits").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const recruitGladiator = async (id) => {
    setBusy(true);
    try { 
      const { data: g } = await api.post(`/recruits/${id}/recruit`); 
      toast.success(`${g.name} joins your ludus!`); 
      await load();
      // Open trait selection modal immediately for the new recruit
      setNewRecruitForTraits(g);
    }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const handleAssignTraits = async (traits) => {
    if (!newRecruitForTraits) return;
    setBusy(true);
    try {
      await api.post(`/gladiators/${newRecruitForTraits.id}/assign-traits`, traits);
      toast.success(`Traits assigned to ${newRecruitForTraits.name}! They're ready for battle.`);
      setNewRecruitForTraits(null);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const refreshPool = async () => {
    setRefreshing(true);
    try {
      const { data: res } = await api.post("/recruits/refresh");
      toast.success(`Recruit pool refreshed for ${res.cost} gold!`, { icon: <GoldCoin size={18} className="text-amber-400" /> });
      await refreshUser();
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setRefreshing(false); }
  };

  if (!data) return <Loader />;
  const reserved = data.reserved_slots || 0;
  const full = data.roster_count + reserved >= data.max_roster;
  const cost = data.refresh_cost ?? 50;
  const canAfford = (data.gold ?? 0) >= cost;
  const freshRecruits = data.recruits.filter((recruit) => recruit.recruit_type !== "veteran");
  const veteranRecruits = data.recruits.filter((recruit) => recruit.recruit_type === "veteran");
  const showFresh = recruitFilter === "all" || recruitFilter === "fresh";
  const showVeterans = recruitFilter === "all" || recruitFilter === "veteran";

  return (
    <div>
      <PageHeader title="Recruit Pool" subtitle="Find raw young talent or add a proven veteran to your ludus"
        icon={UserPlus} />

      <p data-testid="recruit-goal" className="-mt-2 mb-5 text-amber-300 font-cinzel tracking-wide text-base">
        Your goal as a Lanista is to find gladiators with the highest potential!
      </p>

      {/* Potential Helper */}
      <div className="mb-5 -mt-2">
        <button 
          data-testid="recruit-potential-help-button"
          onClick={() => setShowPotentialHelp(!showPotentialHelp)}
          className="inline-flex items-center gap-1.5 text-xs text-orange-400 hover:text-orange-300 transition-colors"
        >
          <HelpCircle size={14} className="text-orange-500" />
          <span className="underline decoration-dotted">What does potential do?</span>
        </button>
        
        {showPotentialHelp && (
          <div data-testid="recruit-potential-help" className="mt-3 p-4 bg-zinc-900/90 border border-orange-700/40 rounded-sm text-sm">
            <div className="flex items-start gap-2 mb-3">
              <HelpCircle size={18} className="text-orange-500 mt-0.5 shrink-0" />
              <h3 className="font-cinzel text-amber-300 uppercase tracking-wide">Understanding Potential</h3>
            </div>
            
            <div className="space-y-3 text-zinc-300 text-xs leading-relaxed">
              <div>
                <p className="font-semibold text-amber-400 mb-1">Training Speed</p>
                <p>Potential does not affect training speed. Each gladiator has their own separate training-speed value.</p>
              </div>
              
              <div>
                <p className="font-semibold text-amber-400 mb-1">Career Length</p>
                <p>Potential determines <strong>when decline starts</strong> — higher potential = longer career:</p>
                <ul className="mt-1.5 ml-4 space-y-1 list-disc text-zinc-400">
                  <li><span className="text-red-400">Poor:</span> Decline at 22 (rapid at 25)</li>
                  <li><span className="text-orange-400">Average:</span> Decline at 24 (rapid at 27)</li>
                  <li><span className="text-yellow-400">Good:</span> Decline at 26 (rapid at 29)</li>
                  <li><span className="text-green-400">Excellent:</span> Decline at 27 (rapid at 30)</li>
                  <li data-testid="recruit-potential-elite-lifecycle"><span className="text-blue-400">Elite:</span> Slow decline at 28 (rapid at 31)</li>
                </ul>
              </div>
              
              <div>
                <p className="font-semibold text-amber-400 mb-1">Strategy</p>
                <p>
                  <strong>High potential</strong> (Good+) = More training time before decline = Higher peak stats<br/>
                  <strong>Low potential</strong> (Poor/Average) = Shorter career, train fast and retire early
                </p>
              </div>
              
              <div className="pt-2 border-t border-zinc-800">
                <p className="text-orange-500 italic">
                  Fresh recruits need scouting to reveal potential. Veteran recruits arrive 70% scouted.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
        <span className="flex items-center gap-2 text-zinc-400 border border-amber-900/40 px-3 py-2 bg-zinc-950">
          <Clock size={15} className="text-amber-500" /> Refreshes in <span className="text-amber-300"><Countdown to={data.refresh_at} /></span>
        </span>
        <span className={`px-3 py-2 border ${full ? "border-red-900/50 text-red-400" : "border-amber-900/40 text-amber-300"} bg-zinc-950`} data-testid="recruit-roster-chip">
          Roster {data.roster_count}/{data.max_roster}{reserved > 0 && ` · ${reserved} reserved by bids`}{full && " — full"}
        </span>
        <span className="flex items-center gap-1.5 text-amber-300 border border-amber-900/40 px-3 py-2 bg-zinc-950" data-testid="recruit-gold">
          <GoldCoin size={15} className="text-amber-500" /> {(data.gold ?? 0).toLocaleString()} gold
        </span>
        <button data-testid="refresh-pool-btn" onClick={refreshPool} disabled={refreshing || !canAfford}
          title={canAfford ? `Reroll the recruit pool for ${cost} gold` : `You need ${cost} gold to refresh`}
          className="inline-flex items-center gap-2 px-4 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm bg-amber-500 text-zinc-950 hover:bg-amber-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : `Refresh Pool · ${cost}g`}
        </button>
        {(data.refreshes_today ?? 0) > 0 && (
          <span data-testid="refreshes-today" className="text-xs text-zinc-500">
            Refreshed {data.refreshes_today}× today{cost >= 100 ? " · max price" : " · next costs more"}
          </span>
        )}
      </div>

      {reserved > 0 && (
        <div data-testid="reserved-slots-note" className="-mt-2 mb-5 flex items-start gap-2 text-sm text-fuchsia-300/90 border border-fuchsia-800/40 bg-fuchsia-950/20 px-3 py-2 rounded-sm">
          <Gavel size={15} className="mt-0.5 shrink-0" />
          <span>You're the top bidder on <strong>{reserved}</strong> gladiator auction{reserved === 1 ? "" : "s"} — {reserved === 1 ? "a slot is" : `${reserved} slots are`} reserved so you can receive {reserved === 1 ? "the gladiator" : "them"} if you win. You can't recruit into {reserved === 1 ? "that slot" : "those slots"} until those bids resolve (you win or get outbid). Retire a gladiator to free more room.</span>
        </div>
      )}

      <div data-testid="recruit-type-filters" className="mb-6 flex flex-wrap gap-2">
        {[["all", `All (${data.recruits.length})`], ["fresh", `Fresh (${freshRecruits.length})`], ["veteran", `Veterans (${veteranRecruits.length})`]].map(([value, label]) => (
          <button key={value} data-testid={`recruit-filter-${value}`} type="button" onClick={() => setRecruitFilter(value)}
            className={`border px-3 py-1.5 text-xs font-cinzel uppercase tracking-wide transition-colors ${recruitFilter === value ? "border-amber-500 bg-amber-500 text-zinc-950" : "border-zinc-700 text-zinc-400 hover:border-amber-700 hover:text-amber-300"}`}>
            {label}
          </button>
        ))}
      </div>

      {data.recruits.length === 0 ? (
        <Empty label="No recruits available. Try a new pool." />
      ) : (
        <div className="space-y-10">
          {showFresh && (
            <section data-testid="fresh-recruits-section">
              <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2 border-b border-zinc-800 pb-2">
                <h2 className="font-cinzel text-lg text-amber-300 uppercase tracking-wide">Fresh Recruits</h2>
                <p className="text-xs text-zinc-500">Age 20 · potential unscouted · best long-term runway</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {freshRecruits.map((recruit) => <RecruitCard key={recruit.id} recruit={recruit} busy={busy} full={full} onRecruit={recruitGladiator} />)}
              </div>
            </section>
          )}
          {showVeterans && (
            <section data-testid="veteran-recruits-section">
              <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2 border-b border-stone-700/70 pb-2">
                <h2 className="font-cinzel text-lg text-stone-200 uppercase tracking-wide">Veteran Recruits</h2>
                <p className="text-xs text-zinc-500">Ages 21–24 · 70% scouted · weapons, armor &amp; helmet included</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {veteranRecruits.map((recruit) => <RecruitCard key={recruit.id} recruit={recruit} busy={busy} full={full} onRecruit={recruitGladiator} />)}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Trait Selection Modal for new recruits */}
      {newRecruitForTraits && (
        <TraitSelectionModal
          gladiator={newRecruitForTraits}
          onConfirm={handleAssignTraits}
          onClose={() => setNewRecruitForTraits(null)}
          required={true}
        />
      )}
    </div>
  );
}
